import { useRef, useLayoutEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { type SpotlightRect } from '../hooks/useSpotlightPosition';

type Placement = 'top' | 'bottom' | 'left' | 'right' | 'center';

interface OnboardingTooltipProps {
  targetRect: SpotlightRect | null;
  placement: Placement;
  children: ReactNode;
  className?: string;
}

const ARROW_SIZE = 8;
const ARROW_PX = `${String(ARROW_SIZE)}px`;
const VIEWPORT_PADDING = 16;

/**
 * Positioned tooltip with arrow, rendered via portal.
 * Calculates position based on target rect and placement.
 * Flips placement if there is not enough viewport space.
 */
export function OnboardingTooltip({
  targetRect,
  placement,
  children,
  className,
}: OnboardingTooltipProps) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    actualPlacement: Placement;
  } | null>(null);

  useLayoutEffect(() => {
    if (!tooltipRef.current) return;

    const tooltip = tooltipRef.current;
    const tooltipRect = tooltip.getBoundingClientRect();

    // Centered placement (no target)
    if (!targetRect || placement === 'center') {
      setPosition({
        top: Math.max(
          VIEWPORT_PADDING,
          (window.innerHeight - tooltipRect.height) / 2
        ),
        left: Math.max(
          VIEWPORT_PADDING,
          (window.innerWidth - tooltipRect.width) / 2
        ),
        actualPlacement: 'center',
      });
      return;
    }

    const centerX = targetRect.x + targetRect.width / 2;
    const centerY = targetRect.y + targetRect.height / 2;

    let top = 0;
    let left = 0;
    let actualPlacement = placement;

    // Calculate position based on placement
    switch (placement) {
      case 'bottom': {
        top = targetRect.y + targetRect.height + ARROW_SIZE + 12;
        left = centerX - tooltipRect.width / 2;
        // Flip to top if not enough space below
        if (top + tooltipRect.height > window.innerHeight - VIEWPORT_PADDING) {
          top = targetRect.y - tooltipRect.height - ARROW_SIZE - 12;
          actualPlacement = 'top';
        }
        break;
      }
      case 'top': {
        top = targetRect.y - tooltipRect.height - ARROW_SIZE - 12;
        left = centerX - tooltipRect.width / 2;
        // Flip to bottom if not enough space above
        if (top < VIEWPORT_PADDING) {
          top = targetRect.y + targetRect.height + ARROW_SIZE + 12;
          actualPlacement = 'bottom';
        }
        break;
      }
      case 'left': {
        top = centerY - tooltipRect.height / 2;
        left = targetRect.x - tooltipRect.width - ARROW_SIZE - 12;
        // Flip to right if not enough space
        if (left < VIEWPORT_PADDING) {
          left = targetRect.x + targetRect.width + ARROW_SIZE + 12;
          actualPlacement = 'right';
        }
        break;
      }
      case 'right': {
        top = centerY - tooltipRect.height / 2;
        left = targetRect.x + targetRect.width + ARROW_SIZE + 12;
        // Flip to left if not enough space
        if (left + tooltipRect.width > window.innerWidth - VIEWPORT_PADDING) {
          left = targetRect.x - tooltipRect.width - ARROW_SIZE - 12;
          actualPlacement = 'left';
        }
        break;
      }
    }

    // Clamp to viewport
    left = Math.max(
      VIEWPORT_PADDING,
      Math.min(left, window.innerWidth - tooltipRect.width - VIEWPORT_PADDING)
    );
    top = Math.max(
      VIEWPORT_PADDING,
      Math.min(top, window.innerHeight - tooltipRect.height - VIEWPORT_PADDING)
    );

    setPosition({ top, left, actualPlacement });
  }, [targetRect, placement]);

  // Calculate arrow position
  const arrowStyle = (): React.CSSProperties | undefined => {
    if (!position || !targetRect || position.actualPlacement === 'center') return undefined;

    const centerX = targetRect.x + targetRect.width / 2;
    const centerY = targetRect.y + targetRect.height / 2;

    switch (position.actualPlacement) {
      case 'bottom':
        return {
          position: 'absolute',
          top: -ARROW_SIZE,
          left: Math.max(12, Math.min(centerX - position.left, 320 - 12 - ARROW_SIZE)),
          width: 0,
          height: 0,
          borderLeft: `${ARROW_PX} solid transparent`,
          borderRight: `${ARROW_PX} solid transparent`,
          borderBottom: `${ARROW_PX} solid hsl(var(--card))`,
        };
      case 'top':
        return {
          position: 'absolute',
          bottom: -ARROW_SIZE,
          left: Math.max(12, Math.min(centerX - position.left, 320 - 12 - ARROW_SIZE)),
          width: 0,
          height: 0,
          borderLeft: `${ARROW_PX} solid transparent`,
          borderRight: `${ARROW_PX} solid transparent`,
          borderTop: `${ARROW_PX} solid hsl(var(--card))`,
        };
      case 'left':
        return {
          position: 'absolute',
          right: -ARROW_SIZE,
          top: Math.max(12, Math.min(centerY - position.top, 200 - 12 - ARROW_SIZE)),
          width: 0,
          height: 0,
          borderTop: `${ARROW_PX} solid transparent`,
          borderBottom: `${ARROW_PX} solid transparent`,
          borderLeft: `${ARROW_PX} solid hsl(var(--card))`,
        };
      case 'right':
        return {
          position: 'absolute',
          left: -ARROW_SIZE,
          top: Math.max(12, Math.min(centerY - position.top, 200 - 12 - ARROW_SIZE)),
          width: 0,
          height: 0,
          borderTop: `${ARROW_PX} solid transparent`,
          borderBottom: `${ARROW_PX} solid transparent`,
          borderRight: `${ARROW_PX} solid hsl(var(--card))`,
        };
    }
  };

  const tooltip = (
    <div
      ref={tooltipRef}
      role="dialog"
      aria-label="Onboarding"
      className={cn(
        'fixed z-[61] w-[320px] max-w-[calc(100vw-32px)] rounded-lg border bg-card text-card-foreground shadow-lg',
        'animate-onboarding-scale-in',
        className
      )}
      style={
        position
          ? { top: position.top, left: position.left }
          : { top: -9999, left: -9999 }
      }
    >
      {position && position.actualPlacement !== 'center' && (
        <div style={arrowStyle()} />
      )}
      {children}
    </div>
  );

  return createPortal(tooltip, document.body);
}
